// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 — see ADDITIONAL-TERMS.md.

import { ALL_ASPECTS, ALL_PALETTES, ASPECT_LABEL, PALETTE_LABEL, resolvePalette } from './theme.ts';
import type { WaveStyle } from './types.ts';

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const STYLES: Array<{ id: WaveStyle; label: string; hint: string }> = [
  { id: 'bars', label: 'Bars', hint: 'Dancing spectrum bars' },
  { id: 'mirror', label: 'Mirror', hint: 'Symmetric spectrum' },
  { id: 'wave', label: 'Waveform', hint: 'Whole clip, sweeping playhead' },
];

function segButtons(name: string, items: Array<{ id: string; label: string; title?: string }>): string {
  return `<div class="seg" role="tablist" data-seg="${name}">${items
    .map(
      (it, i) =>
        `<button class="seg-btn${i === 0 ? ' active' : ''}" data-value="${it.id}"${
          it.title ? ` title="${escapeHtml(it.title)}"` : ''
        }>${escapeHtml(it.label)}</button>`,
    )
    .join('')}</div>`;
}

function paletteSwatches(): string {
  return `<div class="swatches" data-seg="palette">${ALL_PALETTES.map((id, i) => {
    const p = resolvePalette(id);
    const grad = `linear-gradient(135deg, ${p.bars.join(', ')})`;
    return `<button class="swatch${i === 0 ? ' active' : ''}" data-value="${id}" title="${escapeHtml(
      PALETTE_LABEL[id],
    )}" aria-label="${escapeHtml(PALETTE_LABEL[id])}"><span class="swatch-chip" style="background:${grad}"></span><span class="swatch-name">${escapeHtml(
      PALETTE_LABEL[id],
    )}</span></button>`;
  }).join('')}</div>`;
}

const logoSvg = `<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none">
  <rect x="2"  y="9"  width="2.6" height="6"  rx="1.3" fill="url(#lg)"/>
  <rect x="6"  y="6"  width="2.6" height="12" rx="1.3" fill="url(#lg)"/>
  <rect x="10" y="3"  width="2.6" height="18" rx="1.3" fill="url(#lg)"/>
  <rect x="14" y="7"  width="2.6" height="10" rx="1.3" fill="url(#lg)"/>
  <rect x="18" y="10" width="2.6" height="4"  rx="1.3" fill="url(#lg)"/>
  <defs><linearGradient id="lg" x1="0" y1="0" x2="24" y2="0">
    <stop offset="0" stop-color="#ffb020"/><stop offset="0.55" stop-color="#ff5d8f"/><stop offset="1" stop-color="#c057ff"/>
  </linearGradient></defs>
</svg>`;

export interface Refs {
  root: HTMLElement;
  stageIdle: HTMLElement;
  stageEditor: HTMLElement;
  stageResult: HTMLElement;
  dropzone: HTMLElement;
  fileInput: HTMLInputElement;
  pickBtn: HTMLButtonElement;
  sampleBtn: HTMLButtonElement;
  preview: HTMLCanvasElement;
  canvasHolder: HTMLElement;
  playBtn: HTMLButtonElement;
  scrub: HTMLInputElement;
  timeLabel: HTMLElement;
  fileName: HTMLElement;
  changeFile: HTMLButtonElement;
  captionInput: HTMLTextAreaElement;
  sensitivity: HTMLInputElement;
  progressToggle: HTMLInputElement;
  bgInput: HTMLInputElement;
  bgPickBtn: HTMLButtonElement;
  bgClearBtn: HTMLButtonElement;
  renderBtn: HTMLButtonElement;
  resultVideo: HTMLVideoElement;
  downloadBtn: HTMLButtonElement;
  shareBtn: HTMLButtonElement;
  copyBtn: HTMLButtonElement;
  againBtn: HTMLButtonElement;
  resultMeta: HTMLElement;
  drawer: HTMLElement;
  drawerToggle: HTMLButtonElement;
  drawerClose: HTMLButtonElement;
  drawerList: HTMLElement;
  drawerCount: HTMLElement;
  drawerCopy: HTMLButtonElement;
  drawerClear: HTMLButtonElement;
  modalHost: HTMLElement;
  tooltip: HTMLElement;
  renderOverlay: HTMLElement;
  renderBar: HTMLElement;
  renderNote: HTMLElement;
  renderStageLabel: HTMLElement;
  cancelRender: HTMLButtonElement;
}

export function buildShell(root: HTMLElement): Refs {
  root.innerHTML = `
  <header class="topbar">
    <div class="brand">${logoSvg}<span>Wavewell</span></div>
    <nav class="nav">
      <button class="nav-btn" data-modal="how">How it works</button>
      <button class="nav-btn" data-modal="privacy">Privacy</button>
      <button class="nav-btn" data-modal="about">About</button>
      <button class="icon-btn" id="drawer-toggle" title="Event log" aria-label="Event log">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6h16M4 12h16M4 18h10"/></svg>
      </button>
    </nav>
  </header>

  <main class="main-content">
    <section id="stage-idle" class="stage stage-idle">
      <div class="hero">
        <h1>Turn audio into a video you can post</h1>
        <p class="sub">Drop a podcast clip, voice memo or song and Wavewell renders a shareable <span class="glossary-link" data-term="audiogram">audiogram</span> — an animated <span class="glossary-link" data-term="waveform">waveform</span> video — right on your device.</p>
        <button class="trust-badge" id="trust-badge" type="button">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/></svg>
          Runs entirely in your browser · audio never leaves your device
        </button>
      </div>
      <div class="dropzone" id="dropzone" tabindex="0" role="button" aria-label="Choose an audio file">
        <svg class="dz-icon" viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
        <div class="dz-title">Drop an audio file here</div>
        <div class="dz-sub">or tap to choose · MP3, M4A, WAV, OGG, FLAC</div>
        <div class="dz-actions">
          <button class="btn primary" id="pick-btn" type="button">Choose audio</button>
          <button class="pill" id="sample-btn" type="button">Try a sample</button>
        </div>
        <input type="file" id="file-input" accept="audio/*,.mp3,.m4a,.wav,.ogg,.flac,.aac" hidden />
      </div>
      <p class="privacy-note">No upload, no account, no watermark. <span class="glossary-link" data-term="webcodecs">WebCodecs</span> encodes the <span class="glossary-link" data-term="mp4">MP4</span> locally.</p>
    </section>

    <section id="stage-editor" class="stage stage-editor" hidden>
      <div class="editor-grid">
        <div class="preview-wrap">
          <div class="canvas-holder" id="canvas-holder" data-aspect="portrait">
            <canvas id="preview" width="720" height="1280"></canvas>
          </div>
          <div class="transport">
            <button class="btn round" id="play-btn" type="button" aria-label="Play">
              <svg class="i-play" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
              <svg class="i-pause" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" hidden><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>
            </button>
            <input type="range" id="scrub" class="scrub" min="0" max="1000" value="0" aria-label="Scrub" />
            <span class="time-label" id="time-label">0:00 / 0:00</span>
          </div>
          <div class="file-row"><span class="file-chip" id="file-name">—</span><button class="link-btn" id="change-file" type="button">Change file</button></div>
        </div>

        <aside class="rail">
          <div class="field">
            <label class="field-label">Style</label>
            ${segButtons(
              'style',
              STYLES.map((s) => ({ id: s.id, label: s.label, title: s.hint })),
            )}
          </div>
          <div class="field">
            <label class="field-label">Aspect ratio</label>
            ${segButtons(
              'aspect',
              ALL_ASPECTS.map((a) => ({ id: a, label: ASPECT_LABEL[a].split(' · ')[0], title: ASPECT_LABEL[a] })),
            )}
          </div>
          <div class="field">
            <label class="field-label">Palette</label>
            ${paletteSwatches()}
          </div>
          <div class="field">
            <label class="field-label" for="caption">Caption <span class="muted">(optional)</span></label>
            <textarea id="caption" class="caption-input" rows="2" maxlength="140" placeholder="Add a title or quote…"></textarea>
          </div>
          <div class="field">
            <label class="field-label" for="sensitivity">Sensitivity</label>
            <input type="range" id="sensitivity" class="slider" min="0.5" max="2.5" step="0.05" value="1.15" />
          </div>
          <div class="field row">
            <label class="switch"><input type="checkbox" id="progress-toggle" checked /><span>Progress bar</span></label>
          </div>
          <div class="field">
            <label class="field-label">Background image <span class="muted">(optional)</span></label>
            <div class="bg-row">
              <button class="btn ghost" id="bg-pick" type="button">Add image</button>
              <button class="btn ghost" id="bg-clear" type="button" hidden>Remove</button>
              <input type="file" id="bg-input" accept="image/*" hidden />
            </div>
          </div>
          <button class="btn primary render" id="render-btn" type="button">Render video</button>
        </aside>
      </div>
    </section>

    <section id="stage-result" class="stage stage-result" hidden>
      <h2>Your video is ready</h2>
      <div class="result-video-wrap"><video id="result-video" controls playsinline></video></div>
      <div class="result-meta" id="result-meta"></div>
      <div class="result-actions">
        <button class="btn primary" id="download-btn" type="button">Download MP4</button>
        <button class="btn" id="share-btn" type="button" hidden>Share</button>
        <button class="btn ghost" id="copy-btn" type="button">Copy to clipboard</button>
        <button class="btn ghost" id="again-btn" type="button">Make another</button>
      </div>
    </section>
  </main>

  <footer class="site-footer">
    <div class="footer-inner">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </div>
  </footer>

  <aside id="drawer" class="drawer" hidden aria-label="Event log">
    <div class="drawer-head">
      <div class="drawer-title">Event log <span class="count">(<strong id="ev-count">0</strong>)</span></div>
      <button class="icon-btn" id="drawer-close" title="Close" aria-label="Close">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
      </button>
    </div>
    <div class="drawer-list" id="ev-list"></div>
    <div class="drawer-actions">
      <button class="link-btn" id="ev-copy" type="button">Copy log</button>
      <button class="link-btn" id="ev-clear" type="button">Clear</button>
    </div>
  </aside>

  <div id="render-overlay" class="render-overlay" hidden>
    <div class="render-card">
      <div class="render-spinner"></div>
      <div class="render-stage" id="render-stage">Rendering…</div>
      <div class="render-track"><div class="render-bar" id="render-bar"></div></div>
      <div class="render-note" id="render-note">Preparing…</div>
      <button class="btn ghost" id="cancel-render" type="button">Cancel</button>
    </div>
  </div>

  <div id="modal-host" class="modal-host" hidden></div>
  <div id="tooltip" class="tooltip" hidden></div>
  `;

  const q = <T extends HTMLElement>(sel: string): T => {
    const el = root.querySelector<T>(sel);
    if (!el) throw new Error(`missing element ${sel}`);
    return el;
  };

  return {
    root,
    stageIdle: q('#stage-idle'),
    stageEditor: q('#stage-editor'),
    stageResult: q('#stage-result'),
    dropzone: q('#dropzone'),
    fileInput: q<HTMLInputElement>('#file-input'),
    pickBtn: q<HTMLButtonElement>('#pick-btn'),
    sampleBtn: q<HTMLButtonElement>('#sample-btn'),
    preview: q<HTMLCanvasElement>('#preview'),
    canvasHolder: q('#canvas-holder'),
    playBtn: q<HTMLButtonElement>('#play-btn'),
    scrub: q<HTMLInputElement>('#scrub'),
    timeLabel: q('#time-label'),
    fileName: q('#file-name'),
    changeFile: q<HTMLButtonElement>('#change-file'),
    captionInput: q<HTMLTextAreaElement>('#caption'),
    sensitivity: q<HTMLInputElement>('#sensitivity'),
    progressToggle: q<HTMLInputElement>('#progress-toggle'),
    bgInput: q<HTMLInputElement>('#bg-input'),
    bgPickBtn: q<HTMLButtonElement>('#bg-pick'),
    bgClearBtn: q<HTMLButtonElement>('#bg-clear'),
    renderBtn: q<HTMLButtonElement>('#render-btn'),
    resultVideo: q<HTMLVideoElement>('#result-video'),
    downloadBtn: q<HTMLButtonElement>('#download-btn'),
    shareBtn: q<HTMLButtonElement>('#share-btn'),
    copyBtn: q<HTMLButtonElement>('#copy-btn'),
    againBtn: q<HTMLButtonElement>('#again-btn'),
    resultMeta: q('#result-meta'),
    drawer: q('#drawer'),
    drawerToggle: q<HTMLButtonElement>('#drawer-toggle'),
    drawerClose: q<HTMLButtonElement>('#drawer-close'),
    drawerList: q('#ev-list'),
    drawerCount: q('#ev-count'),
    drawerCopy: q<HTMLButtonElement>('#ev-copy'),
    drawerClear: q<HTMLButtonElement>('#ev-clear'),
    modalHost: q('#modal-host'),
    tooltip: q('#tooltip'),
    renderOverlay: q('#render-overlay'),
    renderBar: q('#render-bar'),
    renderNote: q('#render-note'),
    renderStageLabel: q('#render-stage'),
    cancelRender: q<HTMLButtonElement>('#cancel-render'),
  };
}

// ---------- modal content ----------

export const MODAL_CONTENT: Record<string, { title: string; body: string }> = {
  how: {
    title: 'How it works',
    body: `
      <ol class="steps">
        <li><strong>Pick audio.</strong> Drop an MP3, M4A, WAV, OGG or FLAC — or click <em>Try a sample</em>. Your browser decodes it in memory with the Web Audio API. Nothing is uploaded.</li>
        <li><strong>Style it.</strong> Choose a waveform style, an aspect ratio for the platform you’re posting to, a colour palette, an optional caption and an optional background image. The preview updates live.</li>
        <li><strong>Analyse.</strong> Wavewell runs a first-party <span class="glossary-link" data-term="fft">FFT</span> over the sound to drive the dancing bars, or a peak-envelope for the sweeping waveform.</li>
        <li><strong>Render.</strong> A background worker paints every frame to an <span class="glossary-link" data-term="offscreencanvas">OffscreenCanvas</span> and encodes it to <span class="glossary-link" data-term="h264">H.264</span> video + <span class="glossary-link" data-term="aac">AAC</span> audio with <span class="glossary-link" data-term="webcodecs">WebCodecs</span>, muxed into an <span class="glossary-link" data-term="mp4">MP4</span>.</li>
        <li><strong>Save &amp; share.</strong> Download the MP4 or use the native share sheet. It’s ready for Instagram, TikTok, X, LinkedIn or YouTube — no watermark.</li>
      </ol>`,
  },
  privacy: {
    title: 'Privacy &amp; threat model',
    body: `
      <p class="modal-lede">Wavewell is built so your audio has no way to reach a server. Everything happens inside this browser tab.</p>
      <h4 class="ok">Protected</h4>
      <ul>
        <li>Your audio file, the decoded samples, the caption and any background image — all decoded, analysed, drawn and encoded on your device.</li>
        <li>The finished MP4 is created in the tab. It is only ever saved or shared when <em>you</em> click Download or Share.</li>
        <li>No account, no cookies for tracking, no third-party fonts, no upload of any kind.</li>
      </ul>
      <h4 class="warn">Not protected</h4>
      <ul>
        <li>Loading the page itself is a normal web request: GitHub Pages’ CDN sees your IP address and that you fetched the app, like any website. It never receives your audio or video.</li>
        <li>Anyone you <em>choose</em> to send the finished video to, of course, receives it.</li>
      </ul>
      <h4>Trust surface</h4>
      <ul>
        <li>The static site bundle, hash-pinned by the GitHub Pages deploy, and the TLS chain between you and GitHub Pages.</li>
        <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no cross-site tracking; your files and audio are never sent to it.</li>
        <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the feedback form and press Send; your files and audio never are.</li>
      </ul>`,
  },
  about: {
    title: 'About Wavewell',
    body: `
      <p>Wavewell turns audio into a shareable waveform video — an <span class="glossary-link" data-term="audiogram">audiogram</span> — entirely in your browser. It exists because the popular options make you upload unreleased audio to someone else’s server and gate renders behind a subscription. This one runs on your device, for free, with no watermark.</p>
      <p>It’s part of a small collection of privacy-first browser tools. Browse them all at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>
      <p>Made by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a>. Source on <a href="https://github.com/ben-gy/wavewell" target="_blank" rel="noopener">GitHub</a>.</p>
      <p class="muted">Just need to trim audio first? Try <a href="https://clipwell.benrichardson.dev" target="_blank" rel="noopener">Clipwell</a>.</p>`,
  },
};
